// Sync engine (offline mode, Step 5).
//
// Drains the IndexedDB mutation_queue when we're back online. Strategy:
//   - FIFO, one at a time. Sequential matters: a create-then-update on the
//     same entity must replay in order.
//   - Bypass our own offline interceptors so a transient failure doesn't
//     re-queue the same mutation (which would loop forever).
//   - Conflict policy is last-write-wins. If the server returns 409 we resend
//     (client wins) and emit a toast: "Server had newer changes for X — yours
//     were kept." That matches the recommendation locked in during planning.
//   - Backoff: per-mutation `attempts` counter; we drop after maxAttempts to
//     avoid an unkillable loop. The queued record is preserved in `META`
//     under `failed_mutations` so the user can inspect/recover.

// NOTE: we deliberately do NOT import apiClient from App.js here — App.js
// imports this module during init and a circular import would leave one
// side undefined at first read. Callers pass the client into attachSyncEngine.
import {
  listQueue,
  removeFromQueue,
  bumpAttempt,
} from "./mutationQueue";
import {
  setMeta,
  getMeta,
  STORE,
  _internal,
  putEvents,
  getAllEvents,
} from "./offlineCache";

const MAX_ATTEMPTS = 5;
const LAST_SYNCED_KEY = "last_synced_at";
const FAILED_KEY = "failed_mutations";

let _draining = false;
let _onConflict = null; // toast callback, wired by attachSyncEngine
let _onProgress = null; // optional progress callback for UI

const log = (...args) => {
  // eslint-disable-next-line no-console
  if (process.env.NODE_ENV !== "production") console.log("[sync]", ...args);
};

// Replay a single queued mutation. Returns `{ ok, conflict, response, error }`.
const replay = async (client, rec) => {
  try {
    // We rebuild the axios config manually and set `skipOfflineQueue` so the
    // mutation interceptor doesn't re-enqueue a network failure here — that
    // would be an infinite loop. We also keep `skipOfflineCache` off so a
    // server-side fresh response can still mirror into the entity cache.
    const response = await client.request({
      method: rec.method,
      url: rec.url,
      data: rec.data ?? undefined,
      params: rec.params ?? undefined,
      headers: rec.headers || {},
      skipOfflineQueue: true,
    });
    return { ok: true, response };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 409) {
      return { ok: false, conflict: true, error: err };
    }
    if (!err?.response) {
      // Network-level failure — we're offline again. Stop the drain, the
      // online listener will re-trigger it.
      return { ok: false, network: true, error: err };
    }
    // Real server error (4xx/5xx with body). Don't retry forever.
    return { ok: false, error: err, status };
  }
};

// After a successful replay, reconcile local state: replace any optimistic
// _pending=true entity with the server's authoritative copy, and unflag
// _pendingOp markers.
const reconcileAfterSuccess = async (rec, response) => {
  try {
    const data = response?.data;
    if (!data || typeof data !== "object") return;

    // POST /gigs — server gave us a real id; the local entry has a `local-` temp id.
    if (rec.method === "post" && /^\/gigs\/?$/.test(rec.url) && data.id) {
      // Replace any matching temp entity. We don't know the exact temp id here
      // (it lives only in the optimistic patch metadata) so we sweep all
      // _pending creates whose payload matches the request body shape.
      const all = await getAllEvents();
      const temps = all.filter((e) => e._pending && e._pendingOp === "create" && String(e.id).startsWith("local-"));
      const db = await _internal.getDB();
      if (db && temps.length) {
        const tx = db.transaction(STORE.EVENTS, "readwrite");
        // Drop the first matching temp (best-effort dedup).
        await tx.store.delete(temps[0].id);
        await tx.done;
      }
      await putEvents([{ ...data, _pending: false, _pendingOp: null }]);
      return;
    }
    // PUT /gigs/:id — replace with server copy.
    const putMatch = rec.method === "put" && rec.url.match(/^\/gigs\/([^/?]+)$/);
    if (putMatch && data.id) {
      await putEvents([{ ...data, _pending: false, _pendingOp: null }]);
      return;
    }
    // DELETE /gigs/:id — drop the local copy.
    const delMatch = rec.method === "delete" && rec.url.match(/^\/gigs\/([^/?]+)$/);
    if (delMatch) {
      const id = delMatch[1];
      const db = await _internal.getDB();
      if (db) {
        try {
          const tx = db.transaction(STORE.EVENTS, "readwrite");
          await tx.store.delete(id);
          await tx.done;
        } catch {}
      }
      return;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[sync] reconcile failed:", err?.message);
  }
};

const recordFailure = async (rec, reason) => {
  try {
    const existing = (await getMeta(FAILED_KEY)) || [];
    existing.push({ ...rec, droppedAt: Date.now(), reason });
    // Cap stored history.
    await setMeta(FAILED_KEY, existing.slice(-50));
  } catch {}
};

export const drainQueue = async (client) => {
  if (_draining) {
    log("drain already in progress, skipping");
    return { drained: 0, skipped: true };
  }
  _draining = true;
  let drained = 0;
  let conflicts = 0;
  try {
    // Process FIFO. Re-fetch the list each iteration so newly-enqueued items
    // during the drain (e.g. flaky network mid-drain) are picked up.
    // Guarded loop limit so a pathological queue can't spin forever.
    for (let safety = 0; safety < 200; safety += 1) {
      const queue = (await listQueue()).sort((a, b) => a.id - b.id);
      if (queue.length === 0) break;
      const rec = queue[0];
      _onProgress && _onProgress({ phase: "replay", remaining: queue.length, current: rec });

      const result = await replay(client, rec);
      if (result.ok) {
        await reconcileAfterSuccess(rec, result.response);
        await removeFromQueue(rec.id);
        drained += 1;
        continue;
      }
      if (result.conflict) {
        // Last-write-wins: resend forcing the write through. We re-attempt
        // once with a special header the backend can recognise (informational
        // only — server may ignore). If that also conflicts we drop and toast.
        conflicts += 1;
        const retry = await replay(client, {
          ...rec,
          headers: { ...(rec.headers || {}), "X-Fretless-Force-Overwrite": "1" },
        });
        if (retry.ok) {
          await reconcileAfterSuccess(rec, retry.response);
          await removeFromQueue(rec.id);
          drained += 1;
          _onConflict && _onConflict({ rec, response: retry.response });
          continue;
        }
        // Couldn't force. Drop and notify user.
        await removeFromQueue(rec.id);
        await recordFailure(rec, "conflict_unresolved");
        _onConflict && _onConflict({ rec, unresolved: true });
        continue;
      }
      if (result.network) {
        // We went offline mid-drain. Leave the queue intact; the next online
        // event re-triggers drain.
        log("network failure mid-drain, deferring");
        break;
      }
      // Server-side failure. Bump attempts; drop after MAX_ATTEMPTS.
      await bumpAttempt(rec.id);
      const updated = (await listQueue()).find((q) => q.id === rec.id);
      if (!updated || (updated.attempts || 0) >= MAX_ATTEMPTS) {
        await removeFromQueue(rec.id);
        await recordFailure(rec, `max_attempts(status=${result.status || "unknown"})`);
        continue;
      }
      // Backoff: wait before next attempt of THIS record. Use exponential.
      const delay = Math.min(30_000, 500 * 2 ** (updated.attempts - 1));
      await new Promise((r) => setTimeout(r, delay));
    }
    await setMeta(LAST_SYNCED_KEY, Date.now());
    return { drained, conflicts };
  } finally {
    _draining = false;
    _onProgress && _onProgress({ phase: "done", drained, conflicts });
  }
};

export const getLastSyncedAt = () => getMeta(LAST_SYNCED_KEY);
export const getFailedMutations = () => getMeta(FAILED_KEY);

// Wire the engine. iOS Safari (especially home-screen PWAs) is unreliable
// about firing 'online' and 'focus' — so we combine multiple signals:
//   - The standard `online` / `focus` events (covers desktop + Android).
//   - `visibilitychange` to `visible` — fires on iOS PWA when user
//     re-opens the app from the home screen after flight mode.
//   - `pageshow` — fires when restored from bfcache, which is how iOS
//     Safari often "wakes" a backgrounded PWA.
//   - A 5-second poll of `navigator.onLine` that triggers drain on every
//     false→true transition. This is the safety net for the case the user
//     reported: PWA was offline, network came back, no event fired, save
//     didn't sync until the PWA was closed and reopened.
//
// `onConflict` is the place Step 6 can hook a toast.
export const attachSyncEngine = (client, { onConflict, onProgress } = {}) => {
  _onConflict = onConflict || null;
  _onProgress = onProgress || null;

  const trigger = (reason) => {
    if (!navigator.onLine) return;
    log("trigger drain:", reason);
    drainQueue(client).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[sync] drain crashed:", err?.message);
    });
  };

  window.addEventListener("online", () => trigger("online"));
  window.addEventListener("focus", () => trigger("focus"));
  // visibilitychange: fires on iOS PWA when user returns to the app from
  // home screen — more reliable than 'focus'.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") trigger("visible");
  });
  // pageshow: fires when iOS Safari restores the page from bfcache
  // (e.g. after the PWA was backgrounded for a while).
  window.addEventListener("pageshow", () => trigger("pageshow"));

  // Poll navigator.onLine every 5s and trigger drain on false→true
  // transitions. This is the safety net for iOS PWAs where the 'online'
  // event simply doesn't fire when network is restored.
  let lastSeenOnline = navigator.onLine;
  setInterval(() => {
    const nowOnline = navigator.onLine;
    if (nowOnline && !lastSeenOnline) trigger("poll-transition");
    lastSeenOnline = nowOnline;
  }, 5000);

  // Initial drain on attach in case mutations are sitting from a previous
  // session that closed before sync completed.
  setTimeout(() => trigger("startup"), 1500);

  return { drain: () => drainQueue(client) };
};

